import { Controller } from "@hotwired/stimulus"

// Click-toggled popover menu, in two flavours:
//
//   * no open class  — the menu carries `hidden` and is shown by removing it
//     (a plain menu with no transition, e.g. a booking row's overflow menu)
//   * open class     — the class goes on the controller element and CSS runs
//     the reveal, so an animated menu keeps its transition
//     (data-dropdown-open-class="nav-dropdown--open")
//
// Either way this adds what a CSS `:hover` menu can't do: it opens on click,
// closes on Escape or an outside click, and keeps aria-expanded honest — so
// touch and keyboard users can reach the menu at all.
export default class extends Controller {
  static targets = ["trigger", "menu"]
  static classes = ["open"]

  declare readonly triggerTarget: HTMLButtonElement
  declare readonly menuTarget: HTMLElement
  declare readonly openClass: string
  declare readonly hasOpenClass: boolean

  private onDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) this.close()
  }

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return
    this.close()
    this.triggerTarget.focus()
  }

  connect(): void {
    // Marks the menu as JS-driven. Stylesheets use this to drop any
    // no-JS fallback that would otherwise fight the toggle.
    this.element.setAttribute("data-dropdown-ready", "true")
  }

  disconnect(): void {
    this.element.removeAttribute("data-dropdown-ready")
    this.removeListeners()
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open()
  }

  open(): void {
    if (this.hasOpenClass) {
      this.element.classList.add(this.openClass)
    } else {
      this.menuTarget.hidden = false
    }
    this.triggerTarget.setAttribute("aria-expanded", "true")
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)
  }

  close(): void {
    if (this.hasOpenClass) {
      this.element.classList.remove(this.openClass)
    } else {
      this.menuTarget.hidden = true
    }
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.removeListeners()
  }

  private get isOpen(): boolean {
    return this.hasOpenClass
      ? this.element.classList.contains(this.openClass)
      : !this.menuTarget.hidden
  }

  private removeListeners(): void {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
  }
}
